import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  PanelLeft,
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  KeyRound,
  Settings,
} from 'lucide-react'
import { useLayout } from '@/components/layout/AppLayout'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { cn } from '@/lib/utils'

export function Header() {
  const { toggleSidebar } = useLayout()
  const admin = useAuthStore((s) => s.admin)
  const logout = useAuthStore((s) => s.logout)
  const theme = useUiStore((s) => s.theme)
  const toggleTheme = useUiStore((s) => s.toggleTheme)
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleLogout = async () => {
    setDropdownOpen(false)
    await logout()
    navigate('/login')
  }

  const handleChangePassword = () => {
    setDropdownOpen(false)
    navigate('/change-password')
  }

  const handleSettings = () => {
    setDropdownOpen(false)
    navigate('/settings')
  }

  return (
    <header className={cn(
      'sticky top-0 z-30 flex h-16 items-center justify-between gap-4 px-6',
      'border-b border-[#919eab33] dark:border-[#919eab29]',
      'bg-white/80 dark:bg-[#141a21]/80',
      'backdrop-blur-md',
    )}>
      <div className="flex items-center gap-4">
        <button
          onClick={toggleSidebar}
          className="rounded-md p-1.5 text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-600 transition-colors lg:hidden"
          aria-label="Toggle sidebar"
        >
          <PanelLeft className="h-5 w-5" />
        </button>
        <Breadcrumb />
      </div>

      <div className="flex items-center gap-3">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="relative rounded-md p-2 text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-600 transition-colors"
          aria-label="Toggle theme"
        >
          <motion.div
            key={theme}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {theme === 'light' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </motion.div>
        </button>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#cbeee0] text-sm font-semibold text-[#00765a]">
              {admin?.admin_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium text-surface-900 dark:text-white leading-tight">
                {admin?.admin_name || 'Admin'}
              </p>
              <p className="text-xs text-surface-400 dark:text-surface-500 font-mono leading-tight">
                {admin?.tenant_id?.slice(0, 8) || '—'}
              </p>
            </div>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-surface-400 transition-transform duration-150',
                dropdownOpen && 'rotate-180',
              )}
            />
          </button>

          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.97 }}
                transition={{ duration: 0.12 }}
                className="absolute right-0 top-full mt-1.5 w-56 rounded-lg border border-surface-200 bg-white dark:bg-surface-900 dark:border-[#919eab29] shadow-lg z-50"
              >
                <div className="border-b border-surface-100 dark:border-[#919eab29] px-4 py-3">
                  <p className="text-sm font-medium text-surface-900 dark:text-white">
                    {admin?.admin_name || 'Admin'}
                  </p>
                  <p className="text-xs text-surface-400 mt-0.5">
                    {admin?.sub || '—'}
                  </p>
                </div>
                <div className="p-1.5 space-y-0.5">
                  <button
                    onClick={handleSettings}
                    className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-surface-600 dark:text-[#919eab] hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Configuración
                  </button>
                  <button
                    onClick={handleChangePassword}
                    className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-surface-600 dark:text-[#919eab] hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                  >
                    <KeyRound className="h-4 w-4" />
                    Cambiar contraseña
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-surface-600 dark:text-[#919eab] hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-red-600 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Cerrar sesión
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  )
}
